import Vue from 'vue'

const zeroPad = (num, places) => String(num).padStart(places, '0')

export const toMarlonDate = (value) => {
  return `${value.getFullYear()}-${zeroPad(value.getMonth() + 1, 2)}-${zeroPad(
    value.getDate(),
    2
  )}`
}
Vue.prototype.$toMarlonDate = toMarlonDate

export const toLongDate = (value, withoutDay, locale) => {
  const date = new Date(value)
  const options = {
    month: 'long',
    year: 'numeric'
  }

  if (!withoutDay) {
    options.day = 'numeric'
  }

  return date.toLocaleDateString(locale, options)
}
Vue.prototype.$toLongDate = toLongDate

export const stringToDateTime = (string) => {
  if (!string) {
    return null
  }
  const time = string.split(':')
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), ...time)
}
Vue.prototype.$stringToDateTime = stringToDateTime

export const formatDateTimeToTime = (dateTime) => {
  return dateTime?.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  })
}
Vue.prototype.$formatDateTimeToTime = formatDateTimeToTime

export default (ctx, inject) => {
  inject('todayDate', () => {
    return new Date()
  })
  inject('tomorrowDate', () => {
    const tomorrow = new Date(ctx.$todayDate())
    return new Date(tomorrow.setDate(tomorrow.getDate() + 1))
  })
  inject('yesterdayDate', () => {
    const tomorrow = new Date(ctx.$todayDate())
    return new Date(tomorrow.setDate(tomorrow.getDate() - 2))
  })
  inject('epochToDate', (epoch) => {
    if (!epoch) {
      return null
    }

    return new Date(epoch)
  })
  inject('epochToDateTime', (epoch) => {
    if (typeof epoch !== 'number') {
      return null
    }

    return new Date(epoch * 1000)
  })
  inject(
    'calculateDifferenceBetweenEpochToTimeUnit',
    (startEpoch, _endEpoch, _timeUnit) => {
      const endEpoch = !_endEpoch ? Math.floor(Date.now() / 1000) : _endEpoch
      let timeUnit
      if (!startEpoch) {
        return
      }
      if (!_timeUnit) {
        timeUnit = 'days'
      }
      let duration
      switch (timeUnit) {
        case 'minutes':
          duration = 60
          break
        case 'hours':
          duration = 60 * 60
          break
        case 'days':
          duration = 60 * 60 * 24
          break
      }
      return {
        time: Math.floor((endEpoch - startEpoch) / duration),
        unit: timeUnit
      }
    }
  )
  inject('dateTimeToEpoch', (date) => {
    if (!date) {
      return null
    }
    return new Date(date).setHours(0, 0, 0, 0) / 1000
  })

  inject('stringToDateTime', stringToDateTime)

  inject('dateToStartTimeOfPeriod', (date, period) => {
    const result = new Date(date || Date.now())
    result.setHours(0, 0, 0, 0)

    switch (period) {
      case 'week': {
        // getMonday
        const day = result.getDay()
        const difference = result.getDate() - day + (day === 0 ? -6 : 1)
        result.setDate(difference)
        break
      }
      case 'month':
        result.setDate(1)
        break
      case 'year':
        result.setDate(1) // first day of the month - 1-based
        result.setMonth(0) // first month of the year - 0-based
        break
    }

    return result
  })

  inject('formatMinutesAgo', (epoch) => {
    const now = Date.now()
    const difference = now - epoch * 1000
    const minutes = Math.floor(difference / 1000 / 60)

    if (minutes === 0) {
      return ctx.app.i18n.t('now')
    }

    // `${Math.floor(minutes)} ${ctx.app.i18n.t('min_ago')}`
    return ctx.app.i18n.t('min_ago', [Math.floor(minutes)])
  })
  inject('formatAmPm', (hours) => {
    const ampm = ctx.app.i18n.t(hours >= 12 ? 'pm' : 'am')

    hours = hours % 12
    hours = hours ?? 12 // the hour '0' should be '12'

    return `${hours} ${ampm}`
  })

  inject('formatEpochByPeriod', (epoch, period) => {
    const locale = ctx.app.i18n.locale
    let localeOptions = null

    switch (period) {
      case 'week': {
        // get monday
        const monday = ctx.$epochToDateTime(epoch)
        const day = monday.getDay()
        const difference = monday.getDate() - day + (day === 0 ? -6 : 1)
        monday.setDate(difference)

        // get sunday
        const sunday = new Date(monday)
        sunday.setDate(sunday.getDate() + 6)

        const mondayVal = monday.toLocaleDateString(locale, { day: 'numeric' })
        const sundayVal = sunday.toLocaleDateString(locale, { day: 'numeric' })
        if (monday.getMonth() === sunday.getMonth()) {
          const monthVal = monday.toLocaleDateString(locale, { month: 'long' })

          return `${mondayVal} - ${sundayVal} ${monthVal}`
        }

        const mondayMonthVal = monday.toLocaleDateString(locale, {
          month: 'short'
        })
        const sundayMonthVal = sunday.toLocaleDateString(locale, {
          month: 'short'
        })
        return `${mondayVal} ${mondayMonthVal} - ${sundayVal} ${sundayMonthVal}`
      }
      case 'month':
        localeOptions = {
          year: 'numeric',
          month: 'long'
        }
        break
      case 'year':
        localeOptions = {
          year: 'numeric'
        }
        break
      default:
        localeOptions = {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long'
        }
    }

    const date = ctx.$epochToDateTime(epoch)
    return date.toLocaleDateString(locale, localeOptions)
  })

  inject('formatEpochToDayOfMonth', (epoch) => {
    const locale = ctx.app.i18n.locale
    const localeOptions = { month: 'short', day: 'numeric' }
    const date = ctx.$epochToDateTime(epoch)

    return date.toLocaleDateString(locale, localeOptions)
  })

  inject('formatEpochToMonth', (epoch) => {
    const locale = ctx.app.i18n.locale
    const localeOptions = { month: 'short' }
    const date = ctx.$epochToDateTime(epoch)

    return date.toLocaleDateString(locale, localeOptions)
  })

  inject('formatEpochToDateTime', (epoch) => {
    if (!epoch) {
      return null
    }

    const locale = ctx.app.i18n.locale
    const date = ctx.$epochToDateTime(epoch)

    const dayPart = date.toLocaleDateString(locale, { day: 'numeric' })
    const monthPart = date.toLocaleDateString(locale, { month: 'long' })
    const timePart = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    })

    return `${dayPart} ${monthPart} - ${timePart}`
  })

  inject('formatEpochToDate', (epoch, hasYear) => {
    if (!epoch) {
      return null
    }

    const locale = ctx.app.i18n.locale
    const date = ctx.$epochToDateTime(epoch)

    const dayPart = date.toLocaleDateString(locale, { day: 'numeric' })
    const monthPart = date.toLocaleDateString(locale, { month: 'long' })
    let yearPart = ''

    if (hasYear) {
      yearPart = ` ${date.toLocaleDateString(locale, { year: 'numeric' })}`
    }

    return `${dayPart} ${monthPart}${yearPart}`
  })

  inject('formatEpochToShortDate', (epoch) => {
    if (!epoch) {
      return null
    }

    const locale = ctx.app.i18n.locale
    const date = ctx.$epochToDateTime(epoch)

    if (!date) {
      return null
    }

    return date.toLocaleDateString(locale)
  })

  inject('formatTime', (epoch) => {
    // const locale = ctx.app.i18n.locale
    const now = new Date()
    const startOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    )
    const date = new Date(startOfDay.valueOf() + epoch * 1000)

    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    })
  })

  inject('formatDateTimeToTime', formatDateTimeToTime)

  inject('toLongDate', toLongDate)
  Vue.filter('toLongDate', toLongDate)

  inject('toMarlonDate', toMarlonDate)
  Vue.filter('toMarlonDate', toMarlonDate)
}
